import { ChangeEvent, useContext, useEffect, useState } from "react";
import { TabView, TabPanel } from "primereact/tabview";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  DataTable,
  DataTableRowClickEvent,
  DataTableRowPointerEvent,
} from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { parseQueryString, toQueryString } from "../../common/utils";
import { usePeopleList } from "../../api/queries/people/usePeopleList";
import { PeopleListItem } from "../../api/types/people";
import { userContext } from "../../context/user";
import { useDebounce } from "../../common/useDebounce";
import { InputText } from "primereact/inputtext";
import { FloatingSearchNavigation } from "./FloatingSearchNavigation";
import styles from "../Institutions/styles.module.css";

export default function PeopleList() {
  const navigate = useNavigate();
  const { isAdmin, isPeopleEditor, isProfileManager } = useContext(userContext);
  const queryParams = parseQueryString(useLocation().search);
  const limit = 50;

  const [approvedOffset, setApprovedOffset] = useState(0);
  const [unapprovedOffset, setUnapprovedOffset] = useState(0);
  const [approvedTotal, setApprovedTotal] = useState(0);
  const [unapprovedTotal, setUnapprovedTotal] = useState(0);
  const [sortField, setSortField] = useState(queryParams.sortField);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | undefined>(
    queryParams.sortOrder,
  );
  const [search, setSearch] = useState<string>(queryParams.search || "");
  const [activeIndex, setActiveIndex] = useState(isProfileManager() ? 1 : 0);

  const onSort = (event: any) => {
    const sortOrder = event.sortOrder === 1 ? "asc" : "desc";
    setSortField(event.sortField);
    setSortOrder(sortOrder);
  };

  const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };
  const searchDebounced = useDebounce(search, 500);

  const onApprovedPage = (event: any) => setApprovedOffset(event.first);
  const onUnapprovedPage = (event: any) => setUnapprovedOffset(event.first);

  const { data: approvedList, isLoading: loadingApproved } = usePeopleList({
    approved: !isProfileManager(),
    offset: approvedOffset,
    limit,
    search: searchDebounced,
    sortField,
    sortOrder,
  });

  const { data: unapprovedList, isLoading: loadingUnapproved } = usePeopleList({
    approved: false,
    offset: unapprovedOffset,
    limit,
    search: searchDebounced,
    sortField,
    sortOrder,
  });

  const createPeoplePath = (id: string) => {
    const searchParam = searchDebounced
      ? `?search=${encodeURIComponent(searchDebounced)}`
      : "";
    return `/people/${id}${searchParam}`;
  };

  const openPeopleInNewTab = (id: string) => {
    window.open(createPeoplePath(id), "_blank", "noopener,noreferrer");
  };

  const onRowClick = (event: DataTableRowClickEvent) => {
    const id = event.data.id;

    if (
      event.originalEvent.metaKey ||
      event.originalEvent.ctrlKey ||
      event.originalEvent.shiftKey
    ) {
      event.originalEvent.preventDefault();
      openPeopleInNewTab(id);
      return;
    }

    navigate(createPeoplePath(id));
  };

  const onRowPointerUp = (event: DataTableRowPointerEvent) => {
    if (event.originalEvent.button !== 1) {
      return;
    }

    event.originalEvent.preventDefault();
    openPeopleInNewTab(event.data.id);
  };

  const peopleLink = (row: PeopleListItem, label: string) => (
    <Link
      to={createPeoplePath(row.id)}
      onClick={(event) => event.stopPropagation()}
      onAuxClick={(event) => event.stopPropagation()}
    >
      {label}
    </Link>
  );

  const tFirstName = (row: PeopleListItem) => peopleLink(row, row.first_name);
  const tLastName = (row: PeopleListItem) => peopleLink(row, row.last_name);
  const tVisibility = (row: PeopleListItem) => row.visibility;
  const tNewImportData = (row: PeopleListItem) => (
    <span style={{ color: row.has_new_import_data ? "#d32f2f" : "inherit" }}>
      {row.has_new_import_data ? "Yes" : "No"}
    </span>
  );

  useEffect(() => {
    const queryParamsStr = toQueryString({
      sortField,
      sortOrder,
      search: searchDebounced,
    });

    navigate({ search: queryParamsStr });
  }, [navigate, sortField, sortOrder, searchDebounced]);

  useEffect(() => {
    if (approvedList) {
      setApprovedTotal(approvedList.data.total_count);
    }
  }, [approvedList]);

  useEffect(() => {
    if (unapprovedList) {
      setUnapprovedTotal(unapprovedList.data.total_count);
    }
  }, [unapprovedList]);

  return (
    <div className="col-12">
      <div className="flex py-3">
        <h1 className="m-0">People</h1>

        {(isAdmin() || isPeopleEditor()) && (
          <Button
            onClick={() => navigate("/people/new")}
            className="p-button p-button-main p-button-sm ml-auto"
            label="Add new"
          />
        )}
      </div>

      <div className="mb-3">
        <InputText
          value={search}
          onChange={onSearchChange}
          placeholder="Search"
          className={styles.searchInput}
        />
      </div>
      <TabView
        activeIndex={activeIndex}
        onTabChange={(e) => setActiveIndex(e.index)}
      >
        {!isProfileManager() && (
          <TabPanel header={`Approved (${approvedTotal})`}>
            <DataTable
              paginator={true}
              lazy={true}
              emptyMessage={"No records"}
              first={approvedOffset}
              rows={limit}
              onPage={onApprovedPage}
              totalRecords={approvedTotal ?? 0}
              loading={loadingApproved}
              sortField={sortField}
              sortOrder={sortOrder === "asc" ? 1 : -1}
              value={approvedList?.data.results || []}
              stripedRows={true}
              onSort={onSort}
              className="p-datatable-sm p-datatable-pointer"
              onRowClick={onRowClick}
              onRowPointerUp={onRowPointerUp}
            >
              <Column
                header="First name"
                body={tFirstName}
                field="firstName"
                sortable
              />
              <Column
                header="Last name"
                body={tLastName}
                field="lastName"
                sortable
              />
              <Column header="Visibility" body={tVisibility} />
              <Column
                header="Has new import data"
                body={tNewImportData}
                field="hasNewImportData"
                className="text-center"
                sortable
              />
            </DataTable>
          </TabPanel>
        )}
        <TabPanel header={`Unapproved (${unapprovedTotal})`}>
          <DataTable
            paginator={true}
            lazy={true}
            emptyMessage={"No records"}
            first={unapprovedOffset}
            rows={limit}
            onPage={onUnapprovedPage}
            totalRecords={unapprovedTotal ?? 0}
            loading={loadingUnapproved}
            sortField={sortField}
            sortOrder={sortOrder === "asc" ? 1 : -1}
            value={unapprovedList?.data.results || []}
            stripedRows={true}
            onSort={onSort}
            className="p-datatable-sm p-datatable-pointer"
            onRowClick={onRowClick}
            onRowPointerUp={onRowPointerUp}
          >
            <Column header="First name" body={tFirstName} />
            <Column header="Last name" body={tLastName} />
            <Column header="Visibility" body={tVisibility} />
            <Column
              header="Has new import data"
              body={tNewImportData}
              field="hasNewImportData"
              className="text-center"
              sortable
            />
          </DataTable>
        </TabPanel>
      </TabView>
      <FloatingSearchNavigation visible={!!searchDebounced} />
    </div>
  );
}
